import React, { useState } from "react";
import { Button, Card, Row, Col, Alert, Spinner, ListGroup } from "react-bootstrap";
import axios from "axios";
import { getApiHeaders, getApiUrl, IConversion } from "src/app-telehealth-demo/util/utils";
import { IFormData } from "../RemoteCareDemo";

interface ReviewAndSubmitStepProps {
    formData: IFormData;
    conversion: IConversion;
    onComplete: () => void;
    onBack: () => void;
}

const ReviewAndSubmitStep: React.FC<ReviewAndSubmitStepProps> = ({
    formData,
    conversion,
    onComplete,
    onBack,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [submissionComplete, setSubmissionComplete] = useState(false);

    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const maskCreditCard = (cardNumber: string) => {
        if (!cardNumber) return "";
        const lastFour = cardNumber.slice(-4);
        return `•••• •••• •••• ${lastFour}`;
    };

    const groupMedicalQuestions = (prefillFields: any) => {
        const result: { [key: string]: { [key: string]: any } } = {};

        Object.entries(prefillFields).forEach(([field, value]) => {
            // Skip empty values
            if (value === "" || value === null || value === undefined) return;

            const prefix = field.split("_")[0];
            if (!result[prefix]) {
                result[prefix] = {};
            }
            result[prefix][field] = value;
        });

        return result;
    };

    const formatFieldName = (field: string) => {
        const nameWithoutPrefix = field.split("_").slice(1).join(" ");

        return nameWithoutPrefix
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const formatFieldValue = (value: any) => {
        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }

        if (value === true) return "Yes";
        if (value === false) return "No";
        if (value === "true") return "Yes";
        if (value === "false") return "No";

        return value;
    };

    // Submit all data to the API oh jeez
    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            const selectedServices = conversion.services
                .filter((service: any) =>
                    formData.selectedServiceIds.includes(service.careServiceId),
                )
                .map((service: any) => service.careServiceId);

            const submitData = {
                conversionTrackingUuid: conversion.conversionTrackingUuid,
                // Patient info
                firstName: formData.patientInfo.firstName,
                lastName: formData.patientInfo.lastName,
                dateOfBirth: formData.patientInfo.dateOfBirth,
                email: formData.patientInfo.email,
                phone: formData.patientInfo.phone,
                address: formData.patientInfo.address,
                address2: formData.patientInfo.address2 || null,
                city: formData.patientInfo.city,
                state: formData.patientInfo.state,
                zip: formData.patientInfo.zip,
                // Billing info
                billingAddress: formData.billingInfo.sameAsPersonal
                    ? formData.patientInfo.address
                    : formData.billingInfo.billingAddress,
                billingAddress2: formData.billingInfo.sameAsPersonal
                    ? formData.patientInfo.address2
                    : formData.billingInfo.billingAddress2 || null,
                billingCity: formData.billingInfo.sameAsPersonal
                    ? formData.patientInfo.city
                    : formData.billingInfo.billingCity,
                billingState: formData.billingInfo.sameAsPersonal
                    ? formData.patientInfo.state
                    : formData.billingInfo.billingState,
                billingZip: formData.billingInfo.sameAsPersonal
                    ? formData.patientInfo.zip
                    : formData.billingInfo.billingZip,
                // Payment info
                creditCardNumber: formData.paymentInfo.creditCardNumber,
                expirationDate: formData.paymentInfo.expirationDate,
                cvv: formData.paymentInfo.cvv,
                // Selected services
                careServiceIds: selectedServices,
                prefillFields: formData.prefillFields,
            };

            try {
                await axios.post(getApiUrl("submit-conversion"), submitData, {
                    headers: getApiHeaders(),
                });
                setSubmissionComplete(true);
            } catch (err) {
                console.error("Error submitting order:", err);
                setError("There was an error submitting your order. Please try again.");
            }
        } catch (err) {
            console.error("Error submitting order:", err);
            setError("There was an error submitting your order. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submissionComplete) {
        return (
            <div className="success-page text-center py-5">
                <div className="success-icon mb-4">
                    <i
                        className="bi bi-check-circle-fill text-success"
                        style={{ fontSize: "4rem" }}
                    ></i>
                </div>
                <h2 className="mb-3">Order Confirmed!</h2>
                <p className="lead mb-4">
                    Your order has been successfully submitted. You will receive a confirmation
                    email shortly.
                </p>
                <Card className="mb-4">
                    <Card.Body>
                        <Card.Title>Order Details</Card.Title>
                        <p className="mb-1">
                            <strong>Location:</strong> {conversion.locationName}
                        </p>
                        <p className="mb-1">
                            <strong>Provider:</strong> {conversion.practitionerName}
                        </p>
                        <p className="mb-0">
                            <strong>Tracking ID:</strong> {conversion.conversionTrackingUuid}
                        </p>
                    </Card.Body>
                </Card>
                <Button variant="primary" onClick={onComplete}>
                    Done
                </Button>
            </div>
        );
    }

    return (
        <div className="review-submit-step">
            <h2 className="mb-4">Review Your Information</h2>
            <p className="text-muted mb-4">
                Please review all the information below before submitting your order.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* Patient Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Personal Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Name:</strong> {formData.patientInfo.firstName}{" "}
                                {formData.patientInfo.lastName}
                            </p>
                            <p className="mb-1">
                                <strong>Date of Birth:</strong> {formData.patientInfo.dateOfBirth}
                            </p>
                            <p className="mb-1">
                                <strong>Email:</strong> {formData.patientInfo.email}
                            </p>
                            <p className="mb-1">
                                <strong>Phone:</strong> {formData.patientInfo.phone}
                            </p>
                        </Col>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Address:</strong> {formData.patientInfo.address}
                                {formData.patientInfo.address2 &&
                                    `, ${formData.patientInfo.address2}`}
                            </p>
                            <p className="mb-1">
                                <strong>City:</strong> {formData.patientInfo.city}
                            </p>
                            <p className="mb-1">
                                <strong>State:</strong> {formData.patientInfo.state}
                            </p>
                            <p className="mb-1">
                                <strong>ZIP Code:</strong> {formData.patientInfo.zip}
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Medical History & Lifestyle Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Medical History & Lifestyle</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        {/* Group medical questions by category */}
                        {Object.entries(groupMedicalQuestions(formData.prefillFields)).map(
                            ([category, fields]) => (
                                <Col md={6} key={category} className="mb-3">
                                    <h6 className="border-bottom pb-2 mb-3">
                                        {category === "mh"
                                            ? "Medical History"
                                            : category === "cma"
                                              ? "Medications & Allergies"
                                              : category === "ld"
                                                ? "Lifestyle"
                                                : "Other Information"}
                                    </h6>
                                    {Object.entries(fields).map(([field, value]) => (
                                        <p key={field} className="mb-1">
                                            <strong>{formatFieldName(field)}:</strong>{" "}
                                            {formatFieldValue(value)}
                                        </p>
                                    ))}
                                </Col>
                            ),
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {/* Billing Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Billing Information</h5>
                </Card.Header>
                <Card.Body>
                    {formData.billingInfo.sameAsPersonal ? (
                        <p className="mb-0">Same as personal address</p>
                    ) : (
                        <Row>
                            <Col md={12}>
                                <p className="mb-1">
                                    <strong>Address:</strong> {formData.billingInfo.billingAddress}
                                    {formData.billingInfo.billingAddress2 &&
                                        `, ${formData.billingInfo.billingAddress2}`}
                                </p>
                                <p className="mb-1">
                                    <strong>City:</strong> {formData.billingInfo.billingCity}
                                </p>
                                <p className="mb-1">
                                    <strong>State:</strong> {formData.billingInfo.billingState}
                                </p>
                                <p className="mb-1">
                                    <strong>ZIP Code:</strong> {formData.billingInfo.billingZip}
                                </p>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Payment Information */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Payment Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Credit Card:</strong>{" "}
                                {maskCreditCard(formData.paymentInfo.creditCardNumber)}
                            </p>
                        </Col>
                        <Col md={6}>
                            <p className="mb-1">
                                <strong>Expiration Date:</strong>{" "}
                                {formData.paymentInfo.expirationDate}
                            </p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Selected Services */}
            <Card className="mb-3">
                <Card.Header>
                    <h5 className="mb-0">Selected Services</h5>
                </Card.Header>
                <Card.Body>
                    <ListGroup className="mb-0">
                        {conversion &&
                        conversion.services &&
                        formData.selectedServiceIds.length > 0 ? (
                            conversion.services
                                .filter((service: any) =>
                                    formData.selectedServiceIds.includes(service.careServiceId),
                                )
                                .map((service: any) => (
                                    <ListGroup.Item
                                        key={service.id}
                                        className="d-flex justify-content-between"
                                    >
                                        <span>{service.careServiceName}</span>
                                        <span className="font-weight-bold">
                                            {formatCurrency(service.costInCents)}
                                        </span>
                                    </ListGroup.Item>
                                ))
                        ) : (
                            <p className="mb-0">No services selected</p>
                        )}
                    </ListGroup>
                </Card.Body>
            </Card>

            {/* Provider and Location */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Provider & Location</h5>
                </Card.Header>
                <Card.Body>
                    {conversion && (
                        <>
                            <p className="mb-1">
                                <strong>Provider:</strong> {conversion.practitionerName}
                            </p>
                            <p className="mb-1">
                                <strong>Location:</strong> {conversion.locationName}
                            </p>
                            <p className="mb-1">
                                <strong>Address:</strong> {conversion.locationAddress.fullAddress}
                            </p>
                        </>
                    )}
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack} disabled={isSubmitting}>
                    Back
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            Processing...
                        </>
                    ) : (
                        "Submit Order"
                    )}
                </Button>
            </div>
        </div>
    );
};

export default ReviewAndSubmitStep;
